import React, { useState } from "react"

import { usePlugins } from "../../../recoil/pluginStudio/hooks"
import {
  isCustomView,
  PluginFile,
  UserRole,
} from "../../../recoil/pluginStudio/types"
import { CustomViewsDropdown } from "./CustomViewsDropdown"
import MetadataEditor from "./MetadataEditor"

interface HeaderPanelUIProps {
  title?: string
  pluginName?: string
  isEditMode?: boolean
  onEditMetadata?: () => void
  activeView?: UserRole | string
  onTabChange?: (view: UserRole | string, file?: PluginFile) => void
  onAddView?: (viewName: string, visibleToRole: UserRole) => void
  isLoading?: boolean
  children?: React.ReactNode
}

/**
 * Modal component for adding new views
 */
const AddViewModal: React.FC<{
  isOpen: boolean
  onClose: () => void
  onAddView: (viewName: string, visibleToRole: UserRole) => void
}> = ({ isOpen, onClose, onAddView }) => {
  const [viewName, setViewName] = useState("")
  const [visibleToRole, setVisibleToRole] = useState<UserRole>(UserRole.CUSTOM)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (viewName.trim()) {
      onAddView(viewName.trim(), visibleToRole)
      setViewName("")
      setVisibleToRole(UserRole.CUSTOM)
      onClose()
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 w-96">
        <h3 className="text-lg font-semibold text-white mb-4">Add New View</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              View Name
            </label>
            <input
              type="text"
              value={viewName}
              onChange={(e) => setViewName(e.target.value)}
              placeholder="e.g., Admin, Manager, Staff"
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
            <p className="text-xs text-gray-400 mt-1">
              This will create a file named {viewName}View.tsx
            </p>
          </div>
          <div>
            {/* <label className="block text-sm font-medium text-gray-300 mb-2">
              Visible to Role
            </label> */}
            {/* <select
              value={visibleToRole}
              onChange={(e) => setVisibleToRole(e.target.value as UserRole)}
              className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={UserRole.GUEST}>Guest</option>
              <option value={UserRole.HOST_EVENT_VIEW}>Host Event</option>
              <option value={UserRole.HOST_CONFIG_VIEW}>Host Config</option>
            </select> */}
          </div>
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-300 hover:text-white border border-gray-600 rounded-md hover:bg-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Add View
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

/**
 * Presentation component for the header panel
 */
export const HeaderPanelUI: React.FC<HeaderPanelUIProps> = ({
  title = "Plugin Studio",
  pluginName,
  isEditMode = false,
  onEditMetadata,
  children,
  activeView = UserRole.GUEST,
  onTabChange,
  isLoading,
  onAddView,
}) => {
  const [showAddViewModal, setShowAddViewModal] = useState(false)

  const handleAddView = () => {
    setShowAddViewModal(true)
  }

  const handleCreateView = (viewName: string, visibleToRole: UserRole) => {
    if (onAddView) {
      onAddView(viewName, visibleToRole)
    }
  }

  return (
    <>
      <header className="bg-gray-800 border-b border-gray-700 p-4 flex justify-between items-center">
        <div className="flex items-center space-x-2">
          <h1 className="text-xl font-bold text-white">{title}</h1>
          {pluginName && (
            <div className="flex items-center">
              <span className="mx-2 text-gray-400">•</span>
              <span
                className={`text-gray-200 font-medium ${onEditMetadata ? "cursor-pointer hover:text-white hover:underline" : ""}`}
                onClick={onEditMetadata}
                title={onEditMetadata ? "Click to edit plugin details" : ""}
              >
                {pluginName}
              </span>
              {isEditMode && (
                <span className="ml-2 px-2 py-0.5 bg-blue-600 rounded-full text-xs text-white">
                  Editing
                </span>
              )}
              {onEditMetadata && (
                <button
                  onClick={onEditMetadata}
                  className="ml-2 p-1 text-gray-400 hover:text-white rounded-full hover:bg-gray-700"
                  title="Edit plugin details"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                  </svg>
                </button>
              )}
            </div>
          )}
          {children}
        </div>

        <div className="flex items-center space-x-4">
          {/* View toggle buttons */}
          {onTabChange && (
            <div className="flex items-center mr-6">
              <div className="flex bg-gray-700 rounded overflow-hidden">
                <button
                  className={`px-3 py-1 text-sm ${
                    activeView === UserRole.GUEST
                      ? "bg-blue-600 text-white"
                      : "text-gray-300 hover:bg-gray-600"
                  }`}
                  onClick={() => onTabChange(UserRole.GUEST)}
                >
                  Guest
                </button>
                <button
                  className={`px-3 py-1 text-sm ${
                    activeView === UserRole.HOST_EVENT_VIEW
                      ? "bg-blue-600 text-white"
                      : "text-gray-300 hover:bg-gray-600"
                  }`}
                  onClick={() => onTabChange(UserRole.HOST_EVENT_VIEW)}
                >
                  Host Event
                </button>
                <button
                  className={`px-3 py-1 text-sm ${
                    activeView === UserRole.HOST_CONFIG_VIEW
                      ? "bg-blue-600 text-white"
                      : "text-gray-300 hover:bg-gray-600"
                  }`}
                  onClick={() => onTabChange(UserRole.HOST_CONFIG_VIEW)}
                >
                  Host Config
                </button>
              </div>

              {/* Custom Views Dropdown */}
              <CustomViewsDropdown
                activeView={activeView}
                onViewChange={onTabChange}
                className="ml-2"
                size="md"
              />
              <button
                onClick={handleAddView}
                className="ml-2 px-3 py-1 text-sm text-gray-300 hover:bg-gray-600 bg-gray-700 rounded"
                title="Add new view"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </div>
          )}

          {/* Navigation links */}
          <a href="/" className="text-gray-400 hover:text-white">
            Home
          </a>
          <a href="/explore" className="text-gray-400 hover:text-white">
            Explore
          </a>
          <a
            href="/plugin/studio/creation"
            className="text-gray-400 hover:text-white"
          >
            New Plugin
          </a>
          <a href="/search" className="text-gray-400 hover:text-white">
            Search
          </a>
        </div>
      </header>

      <AddViewModal
        isOpen={showAddViewModal}
        onClose={() => setShowAddViewModal(false)}
        onAddView={handleCreateView}
      />
    </>
  )
}

/**
 * Container component that connects to Recoil state
 */
const HeaderPanel: React.FC = () => {
  const {
    activePlugin,
    updatePlugin,
    setActiveViewType,
    activeFile,
    createCustomView,
    setActiveFile,
  } = usePlugins()
  const [isEditingMetadata, setIsEditingMetadata] = useState(false)

  const handleEditMetadata = () => {
    setIsEditingMetadata(true)
  }

  const handleSaveMetadata = (updatedMetadata: any) => {
    if (activePlugin) {
      updatePlugin(activePlugin.id, {
        manifest: {
          ...activePlugin.manifest,
          ...updatedMetadata,
        },
      })
    }
    setIsEditingMetadata(false)
  }

  const handleTabChange = (tab: UserRole | string, file?: any) => {
    if (activePlugin) {
      // If it's a standard view, use setActiveViewType
      if (
        tab != UserRole.CUSTOM &&
        Object.values(UserRole).includes(tab as UserRole)
      ) {
        setActiveViewType(activePlugin.id, tab as UserRole)
      } else {
        // If it's a custom view (string), we need to find the file and select it
        console.log({ activePlugin, file })
        const customFile = activePlugin.files?.find(
          (_file) => _file.name === file.fileName,
        )
        if (customFile) {
          // For custom views, we need to select the file directly
          console.log(`[HeaderPanel] Switching to custom view: ${tab}`)

          // Set the active file to the custom file
          // Note: This assumes there's a way to set the active file from the HeaderPanel
          // If not, we'll need to implement this through the usePlugins hook

          // Update the plugin's view type to indicate we're viewing a custom file
          setActiveFile(activePlugin.id, customFile.id)

          console.log(`[HeaderPanel] Selected custom file: ${customFile.id}`)
        } else {
          console.warn(`[HeaderPanel] Custom view file not found: ${tab}`)
        }
      }
    }
  }

  const handleAddView = (viewName: string, visibleToRole: UserRole) => {
    if (activePlugin) {
      createCustomView(activePlugin.id, viewName, visibleToRole)
    }
  }

  // If metadata editor is open, render that instead
  if (isEditingMetadata && activePlugin) {
    return (
      <MetadataEditor
        manifest={activePlugin.manifest}
        onSave={handleSaveMetadata}
        onCancel={() => setIsEditingMetadata(false)}
        open={isEditingMetadata}
      />
    )
  }

  // Determine the active view from the active file
  const activeView = (() => {
    if (!activeFile) return UserRole.GUEST

    // Check if it's a custom view by looking at the file name
    if (activeFile.name && isCustomView(activeFile)) {
      return activeFile.name // Return the file name for custom views
    }

    // For standard views, check the type
    if (
      activeFile.type === UserRole.GUEST ||
      activeFile.type === UserRole.HOST_EVENT_VIEW ||
      activeFile.type === UserRole.HOST_CONFIG_VIEW
    ) {
      return activeFile.type
    }

    return UserRole.GUEST
  })()

  return (
    <HeaderPanelUI
      title="Plugin Studio"
      pluginName={activePlugin?.manifest?.name || activePlugin?.name}
      onEditMetadata={activePlugin ? handleEditMetadata : undefined}
      activeView={activeView}
      onTabChange={activePlugin ? handleTabChange : undefined}
      onAddView={handleAddView}
    />
  )
}

export default HeaderPanel
